import { fullscreenActive } from "../ads/fullscreen-policy";

export const BACKGROUND_RETURN_MS = 180_000;
interface BackgroundReturnOptions {
  target: EventTarget;
  visible: () => boolean;
  protectedFlow: () => boolean;
  now: () => number;
  restart: () => void;
}

/** One restart per genuine background period, never an elapsed gameplay timer.
 * Native monotonic durations supplement visibility for suspended WebViews.
 * Ads, authentication and the file chooser are not abandoned game sessions. */
export function bindBackgroundReturn(options: BackgroundReturnOptions): () => void {
  let pausedAt: number | null = null, protectedAtPause = false, claimed = false, disposed = false, picker = false;
  let queuedNativeDuration: number | undefined;
  const blocked = () => picker || options.protectedFlow();
  const pause = () => {
    if (disposed || claimed || pausedAt !== null) return;
    pausedAt = options.now(); protectedAtPause = blocked(); queuedNativeDuration = undefined;
  };
  const resume = (nativeDuration?: number) => {
    if (disposed || claimed || pausedAt === null || !options.visible()) return;
    const elapsed = options.now() - pausedAt;
    const nativeElapsed = nativeDuration ?? queuedNativeDuration;
    const duration = nativeElapsed === undefined ? elapsed
      : Math.max(nativeElapsed, Number.isFinite(elapsed) ? elapsed : 0);
    const excluded = protectedAtPause || blocked();
    pausedAt = null; protectedAtPause = false; picker = false; queuedNativeDuration = undefined;
    if (excluded || !Number.isFinite(duration) || duration < BACKGROUND_RETURN_MS) return;
    claimed = true; options.restart();
  };
  const visibility = () => { if (options.visible()) resume(); else pause(); };
  const nativeResume = (event: Event) => {
    const value: unknown = (event as CustomEvent<{ backgroundDurationMs?: unknown }>).detail?.backgroundDurationMs;
    if (pausedAt !== null && Number.isSafeInteger(value) && (value as number) >= 0) {
      queuedNativeDuration = value as number;
      resume(value as number);
    }
  };
  const pick = (event: Event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest("label");
    if (event.target.closest('input[type="file"]') || label instanceof HTMLLabelElement && label.control instanceof HTMLInputElement && label.control.type === "file") picker = true;
  };
  const picked = (event: Event) => { if (event.target instanceof HTMLInputElement && event.target.type === "file") picker = false; };
  options.target.addEventListener("visibilitychange", visibility);
  options.target.addEventListener("varnax-native-inactive", pause);
  options.target.addEventListener("varnax-native-resume", nativeResume);
  options.target.addEventListener("click", pick, true);
  options.target.addEventListener("change", picked, true);
  options.target.addEventListener("cancel", picked, true);
  if (!options.visible()) pause();
  return () => {
    disposed = true; pausedAt = null;
    options.target.removeEventListener("visibilitychange", visibility);
    options.target.removeEventListener("varnax-native-inactive", pause);
    options.target.removeEventListener("varnax-native-resume", nativeResume);
    options.target.removeEventListener("click", pick, true);
    options.target.removeEventListener("change", picked, true);
    options.target.removeEventListener("cancel", picked, true);
  };
}

export function installBackgroundReturn(): () => void {
  return bindBackgroundReturn({
    target: document, visible: () => !document.hidden, now: () => Date.now(),
    // Ordinary loading cards (including a no-fill banner) must not suppress a
    // genuine background return. Only actual boot/auth/upload flows are exempt.
    protectedFlow: () => fullscreenActive() || Boolean(document.querySelector('.startup-screen, .google-auth-control[aria-busy="true"], .profile-photo-setting[aria-busy="true"]')),
    // RootRouter starts at the menu; no auth, wallet or player storage is cleared.
    restart: () => { window.history.replaceState({}, "", "/"); window.location.reload(); },
  });
}
